import gql from 'graphql-tag';

const GET_PATCH_CATEGORY_SEVERITY_META = gql`
  query($classId: ID!) {
    getPatchCategorySeverityMetaByClassId(classId: $classId) {
      generalMeta {
        categories {
          patchCategoryId
          name
        }
        severities {
          patchSeverityId
          name
        }
      }
      securityMeta {
        categories {
          patchCategoryId
          name
        }
        severities {
          patchSeverityId
          name
        }
      }
    }
  }
`;

const GET_PATCH_ALL_CATEGORY_SEVERITY_META = gql`
  query($classId: ID!) {
    getPatchAllCategorySeverityMetaByClassId(classId: $classId) {
      categories {
        patchCategoryId
        name
      }
      severities {
        patchSeverityId
        name
      }
    }
  }
`;

const CREATE_POLICY_SET = gql`
  mutation($input: PolicySetInput) {
    createPolicySet(input: $input) {
      policySetId
    }
  }
`;

const GET_ALL_POLICY_SET = gql`
  query($category: String) {
    getAllPolicySet(category: $category) {
      policySetId
      name
      description
      category
      assetClass {
        classId
        iconClass
      }
      assetGroups {
        name
      }
    }
  }
`;

const GET_ALL_POLICY_SET_BY_CLASS_ID = gql`
  query($classId: ID!) {
    getAllPolicySetByClassId(classId: $classId) {
      policySetId
      name
      description
      category
      assetClass {
        classId
      }
      assetGroups {
        name
      }
    }
  }
`;

const ASSET_CLASS_POLICY = gql`
  fragment comparisonFields on AssetClassPolicy {
    inherited
    overridden
    overriddenPolicy {
      policyId
    }
  }
`;

const GET_POLICY_SET_DETAIL = gql`
  ${ASSET_CLASS_POLICY}
  query(
    $policySetId: ID!
    $category: PolicySetCategory!
    $clientId: ID
    $siteId: ID
    $assetId: ID
  ) {
    getPolicySet(
      policySetId: $policySetId
      category: $category
      clientId: $clientId
      siteId: $siteId
      assetId: $assetId
    ) {
      policySetId
      name
      description
      category
      policyAssociations {
        policyType
        policies {
          policyId
          name
          ...comparisonFields
        }
      }
    }
  }
`;

const GET_PATCH_POLICY_DETAIL = gql`
  query($policyId: ID!) {
    getPolicy(policyId: $policyId) {
      policyId
      name
      description
      enabled
      patchSchedules {
        scheduleId
        name
        cycleType
        dayOfWeek
        startTime
        scheduleDetail
        installWindowEnabled
        installWindowDuration
        scheduleConditions {
          conditionId
          conditionConfigs {
            mappingId
            patchCategory {
              patchCategoryId
              name
            }
            patchSeverity {
              patchSeverityId
              name
            }
          }
        }
      }
      patchScanSchedules {
        scheduleId
        name
        scheduleDetail
      }
      patchApprovals {
        patchCategory {
          patchCategoryId
        }
        patchSeverity {
          patchSeverityId
        }
        approvalState
      }
      patchRebootConfig {
        rebootOnActiveSession
        rebootTriggerWindowInterval
        rebootOnInactiveSession
        rebootPermissionConfig
        activeRebootWindowInterval
        customizedRebootMessage
      }
      patchConfiguration {
        deferredApprovalConfig
        deployPatchOnAssetOnboard
        wakeOnLan
        osManager
      }
      actions {
        actionType
        actionConfigId
        scheduleDetail
        scriptArguments
        executionType
        skipOnOffline
        script {
          name
          scriptId
        }
        notification {
          id
          name
        }
      }
      overriddenPatchApprovalConfigs {
        configId
        title
        lastUpdatedBy {
          userId
          name
        }
        description
        lastUpdatedTime
        approvalBased
        approvalState
        approvalValue
      }
    }
  }
`;

const UPDATE_POLICY_SET = gql`
  mutation(
    $policySetId: ID!
    $category: PolicySetCategory!
    $input: PolicySetInput!
    $clientId: ID
    $siteId: ID
    $assetId: ID
  ) {
    updatePolicySet(
      policySetId: $policySetId
      category: $category
      input: $input
      clientId: $clientId
      siteId: $siteId
      assetId: $assetId
    ) {
      policySetId
    }
  }
`;

const UPDATE_POLICIES_PATCH = gql`
  mutation updatePolicies($policySetId: ID!, $policyInput: [PolicyInput!]) {
    updatePolicies(policySetId: $policySetId, policyInput: $policyInput) {
      policyId
      name
      description
      enabled
      patchSchedules {
        scheduleId
        name
        cycleType
        dayOfWeek
        startTime
        scheduleDetail
        installWindowEnabled
        installWindowDuration
        scheduleConditions {
          conditionId
          conditionConfigs {
            mappingId
            patchCategory {
              patchCategoryId
              name
            }
            patchSeverity {
              patchSeverityId
              name
            }
          }
        }
      }
      patchScanSchedules {
        scheduleId
        name
        scheduleDetail
      }
      patchApprovals {
        patchCategory {
          patchCategoryId
        }
        patchSeverity {
          patchSeverityId
        }
        approvalState
      }
      patchRebootConfig {
        rebootOnActiveSession
        rebootTriggerWindowInterval
        rebootOnInactiveSession
        rebootPermissionConfig
        activeRebootWindowInterval
        customizedRebootMessage
      }
      patchConfiguration {
        deferredApprovalConfig
        deployPatchOnAssetOnboard
        wakeOnLan
        osManager
      }
      actions {
        actionType
        actionConfigId
        scheduleDetail
        scriptArguments
        executionType
        skipOnOffline
        script {
          name
          scriptId
        }
        notification {
          id
          name
        }
      }
      overriddenPatchApprovalConfigs {
        configId
        title
        lastUpdatedBy {
          userId
          name
        }
        description
        lastUpdatedTime
        approvalBased
        approvalState
        approvalValue
      }
    }
  }
`;

const ADD_POLICIES_PATCH = gql`
  mutation addPolicies(
    $policySetId: ID!
    $policyType: String!
    $policyInput: [PolicyInput!]
    $clientId: ID
    $siteId: ID
    $assetId: ID
  ) {
    addPolicies(
      policySetId: $policySetId
      policyType: $policyType
      policyInput: $policyInput
      clientId: $clientId
      siteId: $siteId
      assetId: $assetId
    ) {
      policyId
      name
      description
      enabled
      patchSchedules {
        scheduleId
        name
        cycleType
        dayOfWeek
        startTime
        scheduleDetail
        installWindowEnabled
        installWindowDuration
        scheduleConditions {
          conditionId
          conditionConfigs {
            mappingId
            patchCategory {
              patchCategoryId
              name
            }
            patchSeverity {
              patchSeverityId
              name
            }
          }
        }
      }
      patchScanSchedules {
        scheduleId
        name
        scheduleDetail
      }
      patchApprovals {
        patchCategory {
          patchCategoryId
        }
        patchSeverity {
          patchSeverityId
        }
        approvalState
      }
      patchRebootConfig {
        rebootOnActiveSession
        rebootTriggerWindowInterval
        rebootOnInactiveSession
        rebootPermissionConfig
        activeRebootWindowInterval
        customizedRebootMessage
      }
      patchConfiguration {
        deferredApprovalConfig
        deployPatchOnAssetOnboard
        wakeOnLan
        osManager
      }
      actions {
        actionType
        actionConfigId
        scheduleDetail
        scriptArguments
        executionType
        skipOnOffline
        script {
          name
          scriptId
        }
        notification {
          id
          name
        }
      }
      overriddenPatchApprovalConfigs {
        configId
        title
        lastUpdatedBy {
          userId
          name
        }
        description
        lastUpdatedTime
        approvalBased
        approvalState
        approvalValue
      }
    }
  }
`;

const DELETE_POLICIES_PATCH = gql`
  mutation deletePolicy($policyId: ID!) {
    deletePolicy(policyId: $policyId)
  }
`;
const LOAD_RMM_APP_PROPERTIES = gql`
  query {
    loadRMMAppProperties
  }
`;

const CONVERT_TO_ASSET_GROUP_BASED_POLICY_MGMGT = gql`
  mutation {
    convertToAssetGroupBasedPolicyManagement
  }
`;

const GET_PREDEFINED_USER_ACTIONS = gql`
  query {
    getPredefinedUserActions {
      scriptId
      name
      runTimeVariables
      runTimeVars
    }
  }
`;

const GET_ASSET_SCRIPT_LIST = gql`
  query($listInfo: ListInfoInput!) {
    getAssetScriptList(listInfo: $listInfo) {
      assetScripts {
        scriptId
        name
        runTimeVars
        runTimeVariables
        runTimeVars
      }
      listInfo {
        totalCount
      }
    }
  }
`;

const GET_ALERT_POLICY_DETAILS = gql`
  query($policyId: ID!) {
    getPolicy(policyId: $policyId) {
      policyId
      name
      description
      enabled
      dataBackupPolicyConfig
      endpointIntegrationConfig {
        integrationInfo {
          integrationId
          name
        }
        configMapping
      }
      alertConfig {
        coolingOffInterval
        resetInterval
        resetIfConditionNotMetInterval
        resetIfConditionNotMet
        criteria
        groupAlerts
      }
      actions {
        actionConfigId
        actionType
        script {
          scriptId
          name
        }
        scriptArguments
        alertName
        notification {
          id
          name
        }
        ticketData {
          dataId
        }
        alertSeverity
      }
      resetActions {
        actionType
        resetTicketStatus
      }
    }
  }
`;

const GET_ASSET_LIST = gql`
  query($classId: ID!, $listInfo: ListInfoInput!) {
    getAssetListV2(classId: $classId, listInfo: $listInfo) {
      assets {
        assetId
        name
        client {
          name
          accountId
        }
        site {
          name
          id
        }
        assetClass {
          name
        }
      }
      listInfo {
        page
        pageSize
        totalCount
        hasMore
      }
    }
  }
`;

const GET_ASSET_POLICIES = gql`
  query($assetId: ID!) {
    getAssetPolicies(assetId: $assetId) {
      policyType
      policies {
        policyId
        name
        enabled
        ... on AssetClassPolicy {
          inherited
          overridden
          overriddenPolicy {
            policyId
          }
        }
      }
    }
  }
`;

const DELETE_POLICY_SET = gql`
  mutation($policySetId: ID!) {
    deletePolicySet(policySetId: $policySetId)
  }
`;

const GET_AV_POLICY = gql`
  query($policyId: ID!) {
    getPolicy(policyId: $policyId) {
      name
      antiVirusConfig {
        avProduct
        config
      }
      endpointIntegrationConfig {
        integrationInfo {
          integrationId
          name
        }
        configMapping
      }
    }
  }
`;

const GET_INTEGRATION_ENTITY_MAPPING = gql`
  query($entityName: String, $integrationRefId: ID) {
    getIntegrationEntityMapping(
      entityName: $entityName
      integrationRefId: $integrationRefId
    )
  }
`;

const BULK_APPROVE_PATCH_BY_POLICYSET_V2 = gql`
  mutation(
    $classId: ID!
    $patchIds: [ID!]
    $policySetIds: [ID!]
    $approvalState: String!
    $install: Boolean!
  ) {
    updatePatchApprovalStatusAndInstallByPolicySets(
      classId: $classId
      patchIds: $patchIds
      policySetIds: $policySetIds
      approvalState: $approvalState
      install: $install
    )
  }
`;
const BULK_APPROVE_PATCH_ASSETS__V2 = gql`
  mutation($patchIds: [ID!], $assetIds: [ID!]) {
    approveAndInstallPatchAssetsV2(patchId: $patchIds, assetIds: $assetIds)
  }
`;

const BULK_APPROVE_PATCH = gql`
  mutation($patchIds: [ID!]) {
    updateMultiPatchApprovalStatus(
      patchIds: $patchIds
      input: { approvalStatus: "Approved" }
    )
  }
`;

const BULK_REJECT_PATCH = gql`
  mutation($patchIds: [ID!]) {
    updateMultiPatchApprovalStatus(
      patchIds: $patchIds
      input: { approvalStatus: "Rejected" }
    )
  }
`;

const BULK_APPROVE_PATCH_IN_ASSET_LEVEL = gql`
  mutation($assetId: ID!, $patchIds: [ID!]) {
    updateAssetMultiPatchApprovalStatus(
      assetId: $assetId
      patchIds: $patchIds
      input: { approvalStatus: "Approved" }
    )
  }
`;

const BULK_REJECT_PATCH_IN_ASSET_LEVEL = gql`
  mutation($assetId: ID!, $patchIds: [ID!]) {
    updateAssetMultiPatchApprovalStatus(
      assetId: $assetId
      patchIds: $patchIds
      input: { approvalStatus: "Rejected" }
    )
  }
`;
const REVERT_TO_HIERARCHICAL_POLICY = gql`
  mutation {
    revertToAssetClassBasedPolicyManagement
  }
`;

const CLONE_POLICY_SET = gql`
  mutation clonePolicySet($policySetId: ID!) {
    clonePolicySet(policySetId: $policySetId)
  }
`;

export {
  GET_PATCH_CATEGORY_SEVERITY_META,
  GET_PATCH_ALL_CATEGORY_SEVERITY_META,
  CREATE_POLICY_SET,
  GET_ALL_POLICY_SET,
  GET_POLICY_SET_DETAIL,
  GET_PATCH_POLICY_DETAIL,
  UPDATE_POLICY_SET,
  LOAD_RMM_APP_PROPERTIES,
  CONVERT_TO_ASSET_GROUP_BASED_POLICY_MGMGT,
  GET_PREDEFINED_USER_ACTIONS,
  GET_ASSET_SCRIPT_LIST,
  GET_ALERT_POLICY_DETAILS,
  GET_ASSET_LIST,
  GET_ASSET_POLICIES,
  DELETE_POLICY_SET,
  GET_AV_POLICY,
  BULK_APPROVE_PATCH,
  BULK_REJECT_PATCH,
  BULK_APPROVE_PATCH_IN_ASSET_LEVEL,
  BULK_REJECT_PATCH_IN_ASSET_LEVEL,
  REVERT_TO_HIERARCHICAL_POLICY,
  CLONE_POLICY_SET,
  GET_ALL_POLICY_SET_BY_CLASS_ID,
  // cm-revamp ,
  BULK_APPROVE_PATCH_BY_POLICYSET_V2,
  BULK_APPROVE_PATCH_ASSETS__V2,
  GET_INTEGRATION_ENTITY_MAPPING,
  UPDATE_POLICIES_PATCH,
  ADD_POLICIES_PATCH,
  DELETE_POLICIES_PATCH
};
